import { Button } from '@mui/material';
import type { Currency } from '@prisma/client';
import { getSelectorAttribute, usageProductVariantSummarySelectors } from '@weft/app-selectors';
import { VyIcon } from '@weft/icons';
import type { ProductType } from '@weft/services';
import { isTiersVolumeProductVariant } from '@weft/services';
import { PRODUCT_TYPES } from '@weft/services';
import type { NewUsageProduct,
  ProductVariantInput,
  UsageProduct, UsageProductInput,
  UsageProductVariant } from '@weft/types';

import { MAX_TO, PRICING_MODELS } from '@weft/types';
import { isNotNullOrUndefined,
  newId,
  raise,
  removeInIndex } from '@weft/utils';
import { formatNumber } from '@weft/utils';
import { getCurrencySymbol } from 'apps/app/src/services/currency-symbol-mapper';
import cn from 'classnames';
import React, { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { EditIcon } from '../../../../../../components/icons';
import { PlusIcon } from '../../../../../../components/icons/PlusIcon';
import { TrashIcon } from '../../../../../../components/icons/TrashIcon';
import { useDialog } from '../../../../../../hooks/use-dialog';
import type { NewPlanForm } from '../../CreatePlanForm/create-plan-form-types/new-plan-form';
import type { PlanInputForm } from '../../CreatePlanForm/create-plan-form-types/plan-input-form';
import { UsageChargeEditDrawer } from '../../UsageChargeEditDrawer/UsageChargeEditDrawer';

type UsageProductVariantSummaryProps = {
  isEditing: boolean;
  product: UsageProduct | NewUsageProduct;
  productIndex: number;
  editable: boolean;
  enterEditMode: () => void;
  isGroupedProduct?: boolean;
  currency?: Currency
};

export const UsageProductVariantSummary: React.FC<UsageProductVariantSummaryProps> = ({
  isEditing,
  product,
  productIndex,
  editable,
  enterEditMode,
  isGroupedProduct,
  currency,
}) => {
  const { isDialogOpen: isDrawerOpen, handleOpen, handleClose } = useDialog();
  const { setValue, getValues } = useFormContext<PlanInputForm, unknown, NewPlanForm>();

  const onAddVariant = () => {
    const newVariant = getDefaultProductVariant(PRODUCT_TYPES.USAGE, product, isFlatRateEnabled);
    const updatedProductVariants = [...product.variants, newVariant] as UsageProductVariant[];
    setValue(`usageProducts.${productIndex}.variants`, updatedProductVariants);
  };

  const onDelete = useCallback(() => {
    const updatedProducts = removeInIndex(getValues('usageProducts'), productIndex);
    setValue('usageProducts', updatedProducts);
  }, [productIndex]);

  const usageVariants = useMemo(() => getValues(`usageProducts.${productIndex}.variants`) ?? [], [getValues, productIndex]);

  const isFlatRateEnabled = useMemo(() => {
    return usageVariants.some((usageVariant) => {
      switch (usageVariant.pricing.pricingModel) {
        case PRICING_MODELS.PerUnit:
          return false;
        case PRICING_MODELS.Tiers:
          return usageVariant.pricing.priceConfiguration?.isFlatRate ?? false;
        default:
          return false;
      }
    });
  }, [usageVariants]);
  
  const addSubProductVisible = editable && isEditing && isGroupedProduct;

  return (
    <>
      {addSubProductVisible && (
        <div className={cn('px-8 pb-4 pt-2.5', { 'bg-product-bk': product.variants?.length % 2 === 0 })}>
          <button
            type="button"
            disabled={!addSubProductVisible}
            className="text-icon enabled:text-style group flex items-center space-x-1 disabled:cursor-default"
            onClick={onAddVariant}
          >
            <PlusIcon className={cn(
              'text-icon  h-6 w-6',
              { 'group-hover:text-style': addSubProductVisible },
            )}
            />
            <label className="cursor-pointer text-sm group-disabled:cursor-default" >
              Add Product Variant
            </label>
          </button>
        </div>
      )}
      <div className={cn('flex items-center justify-between px-8', { 'border-t border-t-line py-3': isEditing }, { 'py-5' : !isEditing })}>
        {isEditing ? (
          <div className="flex items-center gap-2">
            <Button
              className="min-w-fit"
              sx={{
                color: '#576063',
                background: isDrawerOpen ? '#E1E6E8' : '#F0F4F6',
                fontWeight: '500',
                fontSize: '14px',
                boxShadow: 'none',
                columnGap: '10px',
                borderRadius: '6px',
                padding: '6px 12px',
              }}
              onClick={handleOpen}
            >
              <VyIcon
                color="#576063"
                icon="Edit2"
              />
              <span>Modify</span>
            </Button>

            <UsageChargeEditDrawer
              open={isDrawerOpen}
              onClose={handleClose}
              usageProduct={product as UsageProductInput}
            />
          </div>
        ) : (
          <div className="flex space-x-4">
            {isNotNullOrUndefined(product.basePrice) ? (
              <div className="flex items-center space-x-2">
                <label className="text-label">Base Amount</label>
                <p className="text-xl font-semibold">{`$${formatNumber(product.basePrice)}`}</p>
              </div>
            ) : <></>}
            {isNotNullOrUndefined(product.commitment) && isNotNullOrUndefined(product.commitment.amount) ? (
              <div className="flex items-center space-x-2">
                <label className="text-label">Commitment</label>
                <p className="text-xl font-semibold">{`${formatNumber(product.commitment.amount)} ${product.commitment.type?.toLowerCase()}`}</p>
              </div>
            ) : <></>}
            {isNotNullOrUndefined(product.currencyCommitment) ? (
              <div className="flex items-center space-x-2">
                <label className="text-label">Commitment</label>
                <p className="text-xl font-semibold">{`${getCurrencySymbol(currency)} ${formatNumber(product.currencyCommitment)}`}</p>
              </div>
            ) : <></>}
            {isNotNullOrUndefined(product.rateMultiplier) && (
              <div className="flex items-center space-x-2">
                <label className="text-label">Rate Multiplier</label>
                <p className="text-xl font-semibold">{formatNumber(product.rateMultiplier)}</p>
              </div>
            )}
          </div>
        )}
        {editable && (
          <div className="space-x-5.5 fill-icon flex justify-end">
            <TrashIcon
              onClick={() => onDelete()}
              {...getSelectorAttribute(usageProductVariantSummarySelectors.trashButton)}
            />
            {!isEditing && <EditIcon onClick={enterEditMode}/>}
          </div>
        )}
      </div>
    </>
  );
};

const getDefaultProductVariant = (productType: ProductType, product: UsageProduct | NewUsageProduct, isFlatRateEnabled: boolean): ProductVariantInput => {
  const newProductVariant = { id: newId() };
  const isVolume = isTiersVolumeProductVariant(product.variants[0]);

  switch (productType) {
    case PRODUCT_TYPES.FIXED:
      return {
        ...newProductVariant,
        pricing: {
          pricingModel: PRICING_MODELS.FixedOneTime,
        },
      };
    case PRODUCT_TYPES.USAGE:
      return {
        ...newProductVariant,
        pricing: {
          pricingModel: PRICING_MODELS.Tiers,
          priceConfiguration: {
            isFlatRate: isFlatRateEnabled,
            isVolume,
            tiers: [
              { from: 1, to: MAX_TO },
            ],
          },
        },
      };
    default:
      return raise(`Unknown product type: ${productType}`);
  }
};